import { SignalAbortedError } from '../util/internals/console';
import { copyCanvasElement, createCanvasElement, createImage } from '../util/misc/dom';
import type { TSize } from '../typedefs';
import type { EditorRasterLoadOptions, EditorRasterSource, EditorRasterSourceDescriptor } from './types';

export interface ResolvedRasterSource {
  readonly surface: HTMLCanvasElement;
  readonly size: TSize;
  readonly descriptor: EditorRasterSourceDescriptor;
}

const isNodeRuntime = (): boolean =>
  typeof (globalThis as { process?: { versions?: { node?: string } } }).process?.versions?.node === 'string';

const isBlobSource = (value: unknown): value is Blob => typeof Blob !== 'undefined' && value instanceof Blob;

const isImageElementSource = (value: unknown): value is HTMLImageElement =>
  typeof HTMLImageElement !== 'undefined' && value instanceof HTMLImageElement;

const isCanvasElementSource = (value: unknown): value is HTMLCanvasElement =>
  typeof HTMLCanvasElement !== 'undefined' && value instanceof HTMLCanvasElement;

const inferStringSourceKind = (value: string): EditorRasterSourceDescriptor['kind'] =>
  value.startsWith('data:') ? 'base64' : 'url';

const ensureNotAborted = (signal?: AbortSignal, source = 'loadRasterSource'): void => {
  if (signal?.aborted) {
    throw new SignalAbortedError(source);
  }
};

const resolveElementSize = (
  element: Pick<HTMLImageElement, 'width' | 'height'> & Partial<Pick<HTMLImageElement, 'naturalWidth' | 'naturalHeight'>>,
): TSize => ({
  width: Math.max(element.naturalWidth || element.width || 1, 1),
  height: Math.max(element.naturalHeight || element.height || 1, 1),
});

const drawElementToCanvas = (
  element: CanvasImageSource,
  size: TSize,
): HTMLCanvasElement => {
  const canvas = createCanvasElement();
  canvas.width = Math.max(size.width, 1);
  canvas.height = Math.max(size.height, 1);
  canvas.getContext('2d')?.drawImage(element, 0, 0, canvas.width, canvas.height);
  return canvas;
};

const loadImageElement = (
  image: HTMLImageElement,
  src: string,
  signal?: AbortSignal,
): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    ensureNotAborted(signal, 'loadRasterSource');

    if (isNodeRuntime()) {
      image.src = src;
      resolve();
      return;
    }

    const cleanup = (): void => {
      image.onload = null;
      image.onerror = null;
      signal?.removeEventListener('abort', onAbort);
    };

    const onAbort = (): void => {
      cleanup();
      reject(new SignalAbortedError('loadRasterSource'));
    };

    image.onload = () => {
      cleanup();
      resolve();
    };
    image.onerror = () => {
      cleanup();
      reject(new Error(`Failed to load raster source: ${src}`));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    image.src = src;

    if (image.complete) {
      cleanup();
      resolve();
    }
  });

const readBlobAsDataURL = (
  blob: Blob,
  signal?: AbortSignal,
): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    ensureNotAborted(signal, 'loadRasterSource');

    if (typeof FileReader === 'undefined') {
      reject(new Error('Blob raster sources require FileReader or URL.createObjectURL support'));
      return;
    }

    const reader = new FileReader();
    const cleanup = (): void => {
      reader.onload = null;
      reader.onerror = null;
      reader.onabort = null;
      signal?.removeEventListener('abort', onAbort);
    };

    const onAbort = (): void => {
      cleanup();
      reader.abort();
      reject(new SignalAbortedError('loadRasterSource'));
    };

    reader.onload = () => {
      cleanup();
      resolve(String(reader.result || ''));
    };
    reader.onerror = () => {
      cleanup();
      reject(reader.error ?? new Error('Failed to read Blob raster source'));
    };
    reader.onabort = () => {
      cleanup();
      reject(new SignalAbortedError('loadRasterSource'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    reader.readAsDataURL(blob);
  });

const resolveImageElementSource = async (
  element: HTMLImageElement,
  descriptor: EditorRasterSourceDescriptor,
  options: EditorRasterLoadOptions,
): Promise<ResolvedRasterSource> => {
  if (!element.complete && element.src) {
    await loadImageElement(element, element.src, options.signal);
  }
  const size = resolveElementSize(element);
  return {
    surface: drawElementToCanvas(element, size),
    size,
    descriptor,
  };
};

export const resolveRasterSource = async (
  source: EditorRasterSource,
  options: EditorRasterLoadOptions = {},
): Promise<ResolvedRasterSource> => {
  ensureNotAborted(options.signal, 'loadRasterSource');

  if (typeof source === 'string') {
    const image = createImage();
    await loadImageElement(image, source, options.signal);
    const size = resolveElementSize(image);
    return {
      surface: drawElementToCanvas(image, size),
      size,
      descriptor: {
        kind: inferStringSourceKind(source),
        src: source,
      },
    };
  }

  if (isBlobSource(source)) {
    const urlApi = globalThis.URL;
    if (urlApi?.createObjectURL) {
      const objectUrl = urlApi.createObjectURL(source);
      try {
        const resolved = await resolveRasterSource(objectUrl, options);
        return {
          ...resolved,
          descriptor: {
            kind: 'blob',
            mimeType: source.type || undefined,
          },
        };
      } finally {
        urlApi.revokeObjectURL?.(objectUrl);
      }
    }

    const dataUrl = await readBlobAsDataURL(source, options.signal);
    const resolved = await resolveRasterSource(dataUrl, options);
    return {
      ...resolved,
      descriptor: {
        kind: 'blob',
        mimeType: source.type || undefined,
      },
    };
  }

  if (isImageElementSource(source)) {
    return resolveImageElementSource(source, { kind: 'image-element', src: source.currentSrc || source.src || undefined }, options);
  }

  if (isCanvasElementSource(source)) {
    return {
      surface: copyCanvasElement(source),
      size: {
        width: Math.max(source.width || 1, 1),
        height: Math.max(source.height || 1, 1),
      },
      descriptor: {
        kind: 'canvas-element',
      },
    };
  }

  throw new Error('Unsupported raster source');
};
